import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { DateTime } from 'luxon';
import { TriangleAlert } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { useBillingStatus } from '@/hooks/use-billing-status';
import { useSelf } from '@/hooks/use-self';
import { useTenant } from '@/hooks/use-tenant';
import { getLuxonLocale } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BackofficeOnly } from '@/components/backoffice-only';
import { Button } from '@/components/ui/button';

function formatDate(iso: string, locale: Locales) {
  return DateTime.fromISO(iso)
    .setLocale(getLuxonLocale(locale))
    .toLocaleString(DateTime.DATE_SHORT);
}

function isBillingPage(pathname: string, tenantSlug: string) {
  return pathname === `/${tenantSlug}/billing`;
}

export function PastDueLockModal() {
  const tenant = useTenant();
  const self = useSelf();
  const { LL, locale } = useI18nContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useBillingStatus();
  const [bypassed, setBypassed] = useState(false);

  const isBlockingState =
    data?.state === 'blocked_dashboard' || data?.state === 'blocked_apps';
  const onBillingPage = isBillingPage(location.pathname, tenant.slug);
  const open = isBlockingState && !onBillingPage && !bypassed;

  const description =
    data?.state === 'blocked_apps'
      ? LL.pastDueLockModal.appsBlockedDescription()
      : LL.pastDueLockModal.dashboardBlockedDescription({
          date: data?.appsBlockedAt
            ? formatDate(data.appsBlockedAt, locale)
            : '',
        });

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="mb-2 flex justify-center">
            <span className="rounded-full bg-destructive/15 p-3 text-destructive">
              <TriangleAlert className="size-6" />
            </span>
          </div>
          <AlertDialogTitle className="text-center text-xl">
            {LL.pastDueLockModal.title()}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col items-stretch gap-2 sm:flex-col sm:items-stretch">
          <Button
            className="w-full"
            onClick={() => navigate(`/${tenant.slug}/billing`)}
          >
            {LL.pastDueLockModal.goToBillingButton()}
          </Button>
          {self.isBackofficeUser && (
            <Button
              variant="ghost"
              size="sm"
              className="self-center text-muted-foreground"
              onClick={() => setBypassed(true)}
            >
              <BackofficeOnly />
              {LL.pastDueLockModal.bypassButton()}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
